import React, { useEffect, useState } from 'react';
import { Table, Switch, Card, message, Tag, Space } from 'antd';
import { GlobalOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { apiClient } from 'Utils/api';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  isActive: boolean;
  isDefault: boolean;
}

const LanguagesPage: React.FC = () => {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/admin/languages');
      setLanguages(response.data.languages || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Fehler beim Laden der Sprachen');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Sprache',
      key: 'language',
      render: (_: any, record: Language) => (
        <Space>
          <GlobalOutlined style={{ fontSize: 20, color: '#1890ff' }} />
          <div>
            <div style={{ fontWeight: 500 }}>{record.nativeName}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{record.name}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => <Tag color="blue">{code.toUpperCase()}</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: Language) => (
        <Space>
          {record.isDefault && (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Standard
            </Tag>
          )}
          {record.isActive ? (
            <Tag color="green">Aktiv</Tag>
          ) : (
            <Tag color="default">Inaktiv</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Aktiviert',
      key: 'active',
      render: (_: any, record: Language) => (
        <Switch
          checked={record.isActive}
          disabled={record.isDefault}
          onChange={(checked) => handleToggle(record.code, checked)}
        />
      ),
    },
  ];

  const handleToggle = async (code: string, isActive: boolean) => {
    message.info('Sprachen-Aktivierung ist in dieser Version statisch konfiguriert');
    // In Zukunft: API-Call zum Speichern in DB
  };

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 24 }}>Sprachen-Verwaltung</h1>

      <Card>
        <div style={{ marginBottom: 16, color: '#666' }}>
          <p>
            <strong>Hinweis:</strong> Die Standardsprache ist <strong>Deutsch</strong>.
            Aktivierte Sprachen sind für Übersetzungen im System verfügbar.
          </p>
          <p>
            Sie können Übersetzungen für Produkte, Kategorien und Optionen in den jeweiligen
            Detailseiten pflegen. Wählen Sie dort die gewünschte Sprache über die Sprachauswahl aus.
          </p>
        </div>

        <Table
          columns={columns}
          dataSource={languages}
          rowKey="code"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Card style={{ marginTop: 16 }} title="Mehrsprachige Felder">
        <p><strong>Folgende Felder können übersetzt werden:</strong></p>
        <ul>
          <li><strong>Produkte:</strong> Modellname, Beschreibung</li>
          <li><strong>Kategorien:</strong> Name</li>
          <li><strong>Optionen:</strong> Wert/Bezeichnung</li>
        </ul>
        <p style={{ marginTop: 16, color: '#666' }}>
          Öffnen Sie ein Produkt oder eine Kategorie und wählen Sie die Zielsprache über
          den Sprachselektor aus, um Übersetzungen zu pflegen.
        </p>
      </Card>
    </div>
  );
};

export default LanguagesPage;
